import { useEffect, useState } from 'react';
import { Box, Button, Grid, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiContext } from '@/contexts/apiContext';
import ErrorMessage from '@/components/errorMessage';
import ProgressIndicator from '../progressIndicator';
import ReadOnlyField from '../readOnlyField';
import TabPanel from '../tabPanel';
import ConsumedEventsView from './consumedEventsView';
import EmittedEventDefsView from './emittedEventDefsView';
import SettingDefsView from './settingDefsView';

type Props = {};

const AgentDefView = ({}: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();
  const navigate = useNavigate();

  const { agentDefId } = useParams();

  const [loading, setLoading] = useState(false);
  const [agentDef, setAgentdef] = useState<any>({});
  const [selectedTab, setSelectedTab] = useState<any>('consumedEvents');
  const [error, setError] = useState<any>();

  useEffect(() => {
    const getAgentDef = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getAgentDef(agentDefId as string);
        setAgentdef(response);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    if (agentDefId) {
      getAgentDef();
    }
  }, [agentDefId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const close = () => {
    navigate('/administration?adminTab=agents');
  };

  if (loading && !agentDef.id) {
    return <ProgressIndicator />;
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header Bar */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        {error && <ErrorMessage error={error} />}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4, lg: 2 }}>
            <ReadOnlyField label="Id" value={agentDef.id} type="text" />
          </Grid>
          <Grid size={{ xs: 12, md: 4, lg: 3 }}>
            <ReadOnlyField label="Name" value={agentDef.name} type="text" />
          </Grid>
          <Grid size={{ xs: 12, md: 4, lg: 4 }}>
            <ReadOnlyField label="Description" value={agentDef.description} />
          </Grid>
          <Grid size={{ xs: 12, md: 4, lg: 3 }}>
            <ReadOnlyField label="Domain" value={agentDef.domainId || 'general'} />
          </Grid>
          <Grid size={{ xs: 12, md: 4, lg: 3 }}>
            <ReadOnlyField label="Implementation Stack" value={agentDef.implementationStack} />
          </Grid>
          <Grid size={{ xs: 12, md: 4, lg: 5 }}>
            <ReadOnlyField label="Implementation Class" value={agentDef.implementationClass} type="text" />
          </Grid>
          <Grid size={{ xs: 12, md: 4, lg: 4 }}>
            <ReadOnlyField label="Instance Icon" value={agentDef.instanceIcon} type="text" />
          </Grid>
        </Grid>
      </Box>

      {/* Tabs Bar */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          indicatorColor="secondary"
          variant="scrollable"
          sx={{ minHeight: 40 }}
        >
          <Tab
            value={'consumedEvents'}
            label={
              <Typography variant="caption" sx={{ fontWeight: selectedTab === 'consumedEvents' ? 700 : 500 }}>
                Events Consumed
              </Typography>
            }
            sx={{ minHeight: 40, textTransform: 'none' }}
          />
          <Tab
            value={'emittedEventDefs'}
            label={
              <Typography variant="caption" sx={{ fontWeight: selectedTab === 'emittedEventDefs' ? 700 : 500 }}>
                Events Emitted
              </Typography>
            }
            sx={{ minHeight: 40, textTransform: 'none' }}
          />
          <Tab
            value={'settingDefs'}
            label={
              <Typography variant="caption" sx={{ fontWeight: selectedTab === 'settingDefs' ? 700 : 500 }}>
                Settings
              </Typography>
            }
            sx={{ minHeight: 40, textTransform: 'none' }}
          />
        </Tabs>
      </Box>

      {/* Content Area */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <TabPanel value="consumedEvents" currentTab={selectedTab} sx={{ flex: 1, overflow: 'auto', p: 1 }}>
          <ConsumedEventsView consumedEvents={agentDef.consumedEvents} />
        </TabPanel>
        <TabPanel value="emittedEventDefs" currentTab={selectedTab} sx={{ flex: 1, overflow: 'auto', p: 1 }}>
          <EmittedEventDefsView emittedEventDefs={agentDef.emittedEventDefs} />
        </TabPanel>
        <TabPanel value="settingDefs" currentTab={selectedTab} sx={{ flex: 1, overflow: 'auto', p: 1 }}>
          <SettingDefsView settingDefs={agentDef.settingDefs} />
        </TabPanel>
      </Box>

      {/* Footer Action Bar */}
      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1.5}
        sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}
      >
        <Button
          size="small"
          color="secondary"
          variant="contained"
          onClick={close}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none' }}
        >
          Close
        </Button>
      </Stack>
    </Box>
  );
};

export default AgentDefView;
